import { Button, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import {
  useStartHomeHQPurchaseClaim,
  useVerifyHomeHQPurchaseClaim,
} from '@/hooks/useHomeHQAccess';

const errorMessageOf = (error: unknown) =>
  (error as any)?.response?.data?.message as string | undefined;

/**
 * "I already bought HomeHQ" for a purchase made outside the app under a
 * different email: we send a code to that email, and entering it links the
 * purchase. (Purchases made with this account's email link automatically.)
 */
export const ClaimPurchaseForm = ({ onCancel }: { onCancel: () => void }) => {
  const start = useStartHomeHQPurchaseClaim();
  const verify = useVerifyHomeHQPurchaseClaim();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  const sendCode = () =>
    start.mutate(email.trim(), {
      onSuccess: () => {
        setSentTo(email.trim());
        setCode('');
        verify.reset();
      },
    });

  if (!sentTo) {
    return (
      <Stack
        component="form"
        spacing={2}
        onSubmit={(e) => {
          e.preventDefault();
          sendCode();
        }}>
        <Typography color="text.secondary" textAlign="center" sx={{ fontSize: 14 }}>
          Enter the email you used to buy HomeHQ. We'll send a code there to confirm it's yours.
        </Typography>
        <TextField
          size="small"
          type="email"
          placeholder="Purchase email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          slotProps={{ htmlInput: { 'aria-label': 'Purchase email', style: { fontSize: 14 } } }}
        />
        <Button
          type="submit"
          fullWidth
          sx={{ fontSize: 14 }}
          disabled={start.isPending || !email.trim()}>
          {start.isPending ? 'Sending…' : 'Send code'}
        </Button>
        {start.isError && (
          <Typography role="alert" variant="caption" color="error" textAlign="center">
            {errorMessageOf(start.error) ?? 'Could not send a code. Please try again.'}
          </Typography>
        )}
        <Button variant="text" sx={{ fontSize: 13 }} onClick={onCancel}>
          Back to plans
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      component="form"
      spacing={2}
      onSubmit={(e) => {
        e.preventDefault();
        verify.mutate(code);
      }}>
      <Typography color="text.secondary" textAlign="center" sx={{ fontSize: 14 }}>
        If {sentTo} has a HomeHQ purchase, we sent it a 6-digit code. It expires in 15 minutes.
      </Typography>
      <TextField
        size="small"
        placeholder="6-digit code"
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        slotProps={{
          htmlInput: {
            'aria-label': 'Verification code',
            inputMode: 'numeric',
            autoComplete: 'one-time-code',
            style: { fontSize: 14, letterSpacing: 4, textAlign: 'center' },
          },
        }}
      />
      <Button
        type="submit"
        fullWidth
        sx={{ fontSize: 14 }}
        disabled={verify.isPending || code.length !== 6}>
        {verify.isPending ? 'Checking…' : 'Link my purchase'}
      </Button>
      {verify.isError && (
        <Typography role="alert" variant="caption" color="error" textAlign="center">
          {errorMessageOf(verify.error) ?? 'Could not check that code. Please try again.'}
        </Typography>
      )}
      <Stack direction="row" justifyContent="center" spacing={1}>
        <Button variant="text" sx={{ fontSize: 13 }} disabled={start.isPending} onClick={sendCode}>
          {start.isPending ? 'Sending…' : 'Resend code'}
        </Button>
        <Button variant="text" sx={{ fontSize: 13 }} onClick={() => setSentTo(null)}>
          Use a different email
        </Button>
      </Stack>
      {start.isError && (
        <Typography role="alert" variant="caption" color="error" textAlign="center">
          {errorMessageOf(start.error) ?? 'Could not send a code. Please try again.'}
        </Typography>
      )}
    </Stack>
  );
};
